import React from "react";
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { LOGO_SERIES, LogoFile, logoPreviewSize } from "./logoSeries";

function LogoTile({
  file,
  selected,
  onSelect,
}: {
  file: LogoFile;
  selected: boolean;
  onSelect?: (file: LogoFile) => void;
}) {
  const toast = useToast();
  const preview = logoPreviewSize(file);

  const copyUrl = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(file.publicUrl);
      toast({ title: "Logo URL copied", status: "success", duration: 2000 });
    } catch (error: any) {
      toast({ title: "Logo URL was not copied", description: error?.message, status: "error", duration: 4000 });
    }
  };

  return (
    <Box
      textAlign="left"
      border="2px solid"
      borderColor={selected ? "blue.500" : "gray.200"}
      borderRadius="lg"
      p={3}
      bg={selected ? "blue.50" : "white"}
      cursor={onSelect ? "pointer" : "default"}
      onClick={onSelect ? () => onSelect(file) : undefined}
    >
      <Box bg="gray.100" borderRadius="md" p={3} display="flex" alignItems="center" justifyContent="center" minH="88px">
        <img
          src={file.previewSrc}
          alt={`${file.seriesLabel} ${file.width} by ${file.height}`}
          width={preview.width}
          height={preview.height}
          style={{ width: preview.width, height: preview.height, objectFit: "contain", display: "block" }}
        />
      </Box>
      <Text mt={2} fontWeight="600" fontSize="sm">
        {file.width}×{file.height}
      </Text>
      <Text fontSize="xs" color="gray.600" noOfLines={2} title={file.publicUrl}>
        {file.publicUrl}
      </Text>
      <Button mt={2} size="sm" variant="outline" onClick={copyUrl}>
        Copy URL
      </Button>
    </Box>
  );
}

export function LogoCatalog({
  selectedUrl,
  onSelect,
}: {
  selectedUrl?: string;
  onSelect?: (file: LogoFile) => void;
}) {
  return (
    <VStack align="stretch" spacing={8}>
      {LOGO_SERIES.map((series) => (
        <Box key={series.id}>
          <Text fontSize="lg" fontWeight="600" mb={4}>
            {series.label}
          </Text>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            Long form
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
            {series.long.map((file) => (
              <LogoTile
                key={file.publicUrl}
                file={file}
                selected={selectedUrl === file.publicUrl}
                onSelect={onSelect}
              />
            ))}
          </SimpleGrid>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            Square form
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            {series.square.map((file) => (
              <LogoTile
                key={file.publicUrl}
                file={file}
                selected={selectedUrl === file.publicUrl}
                onSelect={onSelect}
              />
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </VStack>
  );
}

export function LogoCatalogModal({
  isOpen,
  onClose,
  selectedUrl,
  hint,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedUrl?: string;
  hint: string;
  onSelect: (file: LogoFile) => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="1100px">
        <ModalHeader>Choose logo</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <HStack mb={4}>
            <Text fontSize="sm" color="gray.600">
              {hint}
            </Text>
          </HStack>
          <LogoCatalog
            selectedUrl={selectedUrl}
            onSelect={(file) => {
              onSelect(file);
              onClose();
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
